/**
 * AdditionalServicesSection — the OPTIONAL add-ons on the one-page public
 * "Confirm & book", giving public visitors the same insurance / duties /
 * warehouse fields logged-in portal users get on their booking confirmation
 * (FInalView.js). Presentational only: the two reusable, self-connected
 * children (CargoInsurance, DutyAndTax) pull their own Redux actions/state;
 * this section just supplies the shared antd `form`, the search context, and
 * lifts their values up via the on* callbacks so ConfirmAndBook can fold them
 * into the total + the booking payload.
 *
 *  • Cargo insurance — instant Breeze premium estimate (public API path).
 *  • Duties & taxes — SimplyDuty HS-code + duty/tax estimate (public API path).
 *  • Warehousing — a LIGHTWEIGHT request (toggle + note): there is no public
 *    RFQ backend, so it rides into the booking + the sales@ notification for
 *    the team to follow up (Neil, 2026-07-07), not a spawned RFQ record.
 *
 * All three are OPTIONAL and never gate the CTA — skipping them books at the
 * headline rate, preserving the high-conversion single-action flow.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Checkbox, Input, Spin } from 'antd';

import CargoInsurance from '../CargoInsurance';
import DutyAndTax from '../DutyAndTax';
import {
  SectionCard,
  SectionHeading,
  SectionHint,
  fieldLabelStyle,
} from './confirmTheme';
import { color } from '../../../styles/designTokens';

const money = (value) =>
  `$${Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const subtotalRow = (labelText, value) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '4px 0',
      fontSize: 13,
      color: color.ink,
    }}
  >
    <span>{labelText}</span>
    <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
      {money(value)}
    </span>
  </div>
);

const AdditionalServicesSection = ({
  form,
  queryParams,
  finalSearchForm,
  commodityListLoaded,
  commodityArr,
  insuranceArr,
  insurancePremium,
  dutyTax,
  dutyTaxSubtotal,
  warehouseRequested,
  warehouseNote,
  onInsuranceAmountChange,
  onCommodityChange,
  onAddCommodity,
  onDeleteCommodity,
  onDutyTaxChange,
  onAddDutyTax,
  onDeleteDutyTax,
  onDutyTaxAmountSubtotalChange,
  onWarehouseToggle,
  onWarehouseNote,
}) => (
  <>
    <SectionCard aria-label="Cargo insurance (optional)">
      <SectionHeading>Cargo insurance</SectionHeading>
      <SectionHint>
        Optional — get an instant premium to protect this shipment. Skip to book
        without it.
      </SectionHint>
      {commodityListLoaded ? (
        <>
          <CargoInsurance
            form={form}
            commodityArr={commodityArr}
            queryParams={queryParams}
            final_search_form={finalSearchForm}
            onInsuranceAmountChange={onInsuranceAmountChange}
            onCommodityChange={onCommodityChange}
            onAddCommodity={onAddCommodity}
            onDeleteCommodity={onDeleteCommodity}
          />
          {insuranceArr && insuranceArr.length > 0 && (
            <div style={{ marginTop: 8 }}>
              {subtotalRow('Estimated total premium', insurancePremium)}
            </div>
          )}
        </>
      ) : (
        <div style={{ padding: '12px 0' }}>
          <Spin size="small" />{' '}
          <span style={{ fontSize: 12.5, color: color.ink2 }}>
            Loading insurance options…
          </span>
        </div>
      )}
    </SectionCard>

    <SectionCard aria-label="Duties and taxes (optional)">
      <SectionHeading>Duties &amp; taxes</SectionHeading>
      <SectionHint>
        Optional — estimate import duties and taxes for your goods. For guidance
        only; final amounts are set by customs.
      </SectionHint>
      <DutyAndTax
        form={form}
        dutyTax={dutyTax}
        queryParams={queryParams}
        final_search_form={finalSearchForm}
        onDutyTaxChange={onDutyTaxChange}
        onAddDutyTax={onAddDutyTax}
        onDeleteDutyTax={onDeleteDutyTax}
        onDutyTaxAmountSubtotalChange={onDutyTaxAmountSubtotalChange}
      />
      <div style={{ marginTop: 12 }}>
        {subtotalRow('Duties', dutyTaxSubtotal.duties)}
        {subtotalRow('Taxes', dutyTaxSubtotal.taxes)}
      </div>
    </SectionCard>

    <SectionCard aria-label="Warehousing (optional)">
      <SectionHeading>Warehousing &amp; storage</SectionHeading>
      <SectionHint>
        Optional — need storage, handling, or repackaging at destination? Ask us
        and our team will follow up with a quote.
      </SectionHint>
      <Checkbox
        checked={warehouseRequested}
        onChange={(e) => onWarehouseToggle(e.target.checked)}
      >
        <span style={{ fontSize: 13, color: color.ink }}>
          Request warehousing / storage services
        </span>
      </Checkbox>
      {warehouseRequested && (
        <div style={{ marginTop: 12 }}>
          <span style={fieldLabelStyle}>What do you need? (optional)</span>
          <Input.TextArea
            rows={3}
            maxLength={1000}
            value={warehouseNote}
            onChange={(e) => onWarehouseNote(e.target.value)}
            placeholder="e.g. storage for ~20 pallets for 2 weeks, plus repackaging"
            style={{ marginTop: 6 }}
          />
        </div>
      )}
    </SectionCard>
  </>
);

AdditionalServicesSection.propTypes = {
  form: PropTypes.object.isRequired,
  queryParams: PropTypes.object,
  finalSearchForm: PropTypes.object,
  commodityListLoaded: PropTypes.bool,
  commodityArr: PropTypes.array,
  insuranceArr: PropTypes.array,
  insurancePremium: PropTypes.number,
  dutyTax: PropTypes.array,
  dutyTaxSubtotal: PropTypes.shape({
    duties: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    taxes: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  warehouseRequested: PropTypes.bool,
  warehouseNote: PropTypes.string,
  onInsuranceAmountChange: PropTypes.func.isRequired,
  onCommodityChange: PropTypes.func.isRequired,
  onAddCommodity: PropTypes.func.isRequired,
  onDeleteCommodity: PropTypes.func.isRequired,
  onDutyTaxChange: PropTypes.func.isRequired,
  onAddDutyTax: PropTypes.func.isRequired,
  onDeleteDutyTax: PropTypes.func.isRequired,
  onDutyTaxAmountSubtotalChange: PropTypes.func.isRequired,
  onWarehouseToggle: PropTypes.func.isRequired,
  onWarehouseNote: PropTypes.func.isRequired,
};

export default AdditionalServicesSection;
